/**
 * ShareMenu — universal share popover for any URL on Tap In TV.
 *
 * Drop-in component: pass a `url`, `title`, and (optionally) `text` and
 * this renders a horizontal row of share buttons that opens each
 * platform's native compose flow. On mobile it also exposes the OS
 * share sheet via `navigator.share` so users can send straight to
 * Instagram Stories, DMs, Signal, Messages, etc.
 *
 * Supported platforms:
 *   • Native OS share sheet (`navigator.share` — mobile + Safari)
 *   • X / Twitter                   • WhatsApp
 *   • Facebook                       • Reddit
 *   • LinkedIn                       • Telegram
 *   • Email (mailto)                 • Copy-link
 *
 * Usage:
 *   <ShareMenu url={window.location.href} title="Watch me live on Tap IN" />
 */
import React, { useState } from "react";
import {
  Twitter, Facebook, Linkedin, Mail, MessageCircle, Send as TelegramIcon,
  MessageSquare, Copy, Share2, Check,
} from "lucide-react";
import { toast } from "sonner";

export default function ShareMenu({
  url,
  title = "Tap IN TV Network",
  text = "",
  compact = false,
  testidPrefix = "share",
}) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = text || title;

  const open = (u) => window.open(u, "_blank", "noopener,noreferrer");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Link copied — paste it anywhere.");
    } catch { toast.error("Couldn't copy link."); }
  };

  const native = async () => {
    if (typeof navigator === "undefined" || !navigator.share) return copy();
    try { await navigator.share({ title, text: shareText, url: shareUrl }); }
    catch { /* user cancelled */ }
  };

  const platforms = [
    { k: "twitter",  Icon: Twitter,       label: "X",         cls: "hover:bg-black/60",
      onClick: () => open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`) },
    { k: "facebook", Icon: Facebook,      label: "Facebook",  cls: "hover:bg-blue-600/40",
      onClick: () => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`) },
    { k: "whatsapp", Icon: MessageCircle, label: "WhatsApp",  cls: "hover:bg-emerald-500/40",
      onClick: () => open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`) },
    { k: "telegram", Icon: TelegramIcon,  label: "Telegram",  cls: "hover:bg-sky-500/40",
      onClick: () => open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`) },
    { k: "reddit",   Icon: MessageSquare, label: "Reddit",    cls: "hover:bg-orange-500/40",
      onClick: () => open(`https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`) },
    { k: "linkedin", Icon: Linkedin,      label: "LinkedIn",  cls: "hover:bg-sky-700/40",
      onClick: () => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`) },
    { k: "email",    Icon: Mail,          label: "Email",     cls: "hover:bg-white/15",
      onClick: () => {
        const subject = encodeURIComponent(title);
        const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      } },
  ];

  return (
    <div data-testid={`${testidPrefix}-menu`} className="flex flex-wrap items-center gap-1.5">
      {/* Native OS share — highest hit rate on mobile, gives access to
          Instagram Stories / DMs / SMS / anything installed. */}
      {typeof navigator !== "undefined" && "share" in navigator && (
        <button
          type="button"
          onClick={native}
          data-testid={`${testidPrefix}-native`}
          className="inline-flex items-center gap-1.5 bg-tv-accent hover:bg-yellow-400 text-black text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full"
          title="Share to any app on your device"
        >
          <Share2 className="w-3.5 h-3.5" /> {compact ? "Share" : "Share to…"}
        </button>
      )}
      {platforms.map(({ k, Icon, label, onClick, cls }) => (
        <button
          key={k}
          type="button"
          onClick={onClick}
          data-testid={`${testidPrefix}-${k}`}
          title={`Share on ${label}`}
          className={`inline-flex items-center gap-1 text-white text-[11px] font-bold px-2.5 py-1.5 rounded-full border border-white/15 bg-white/5 transition ${cls}`}
        >
          <Icon className="w-3.5 h-3.5" />
          {!compact && <span>{label}</span>}
        </button>
      ))}
      <button
        type="button"
        onClick={copy}
        data-testid={`${testidPrefix}-copy`}
        title="Copy link"
        className="inline-flex items-center gap-1 text-white text-[11px] font-bold px-2.5 py-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/15 transition"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        {!compact && <span>{copied ? "Copied" : "Copy"}</span>}
      </button>
    </div>
  );
}
